import { VoteApi } from '../../api/vote/VoteApi';
import {
    AnonymousUser,
    AnonymousUserContextType,
} from '../../contexts/anonUser';
import { Session, SessionContextType } from '../../contexts/session';
import { PollRecord } from '../../models/Poll';
import Logger from '../../utils/logger';

class PollController {
    public static forceOptionVotes = (poll: PollRecord): PollRecord => {
        const newPoll = { ...poll };

        newPoll.options[0].votes = newPoll.options[0].votes ?? 0;
        newPoll.options[1].votes = newPoll.options[1].votes ?? 0;

        return newPoll;
    };

    /**
     * Will convert (round) 1000 to 1K, and 1000000 to 1M. It will leave 1
     * decimal number when such it's not 0.
     *
     * For example: 1100 > 1.1K, 1000 > 1K, 1190 > 1.2K
     *
     * @param num Number to be converted
     * @returns Number as a tring in a short version
     */
    public static numberToShortString = (num: number): string => {
        const postfix = num > 1000 ? (num > 1000000 ? 'M' : 'K') : '';
        const shortNum = postfix
            ? postfix === 'M'
                ? num / 1000000
                : num / 1000
            : num;

        return (
            (Math.round((shortNum + Number.EPSILON) * 10) / 10).toString() +
            postfix
        );
    };

    /**
     * Return true if it can find a vote in a signed in session or anonymous
     * session.
     *
     * @param pollId Poll id
     * @param session Session object
     * @param anonUser Anonymous session object
     *
     * @returns True if vote found, false otherwise
     */
    public static voted = (
        pollId: string,
        session: Session,
        anonUser: AnonymousUser,
    ): boolean => {
        return (
            (session.votes !== undefined &&
                session.votes[pollId] !== undefined) ||
            (anonUser.votes !== undefined &&
                anonUser.votes[pollId] !== undefined)
        );
    };

    public static processVote = async (
        poll: PollRecord,
        option: number,
        { session, setSession }: SessionContextType,
        setPoll: React.Dispatch<React.SetStateAction<PollRecord>>,
        { anonUser, setAnonUser }: AnonymousUserContextType,
        setVoteEnabled: React.Dispatch<React.SetStateAction<boolean>>,
    ): Promise<void> => {
        setVoteEnabled(false);

        if (session.user) {
            const result = await VoteApi.create(
                {
                    pollId: poll.id,
                    optionId: option.toString(),
                },
                session,
            );

            if (result && result.id) {
                const newSession: Session = {
                    ...session,
                    votes: {
                        ...session.votes,
                        [result.pollId]: {
                            voteId: result.id,
                            optionId: result.optionId,
                        },
                    },
                };
                setSession(newSession);

                const newPoll = { ...poll };
                newPoll.options[option - 1].votes++;
                setPoll(newPoll);
            } else {
                // Maybe we should enable voting again for a retry?
                Logger.error(JSON.stringify(result));
            }
        } else if (anonUser.acceptCookies && anonUser.user) {
            const result = await VoteApi.createAnonymous({
                pollId: poll.id,
                optionId: option.toString(),
                userId: anonUser.user.id,
            });

            if (result && result.id) {
                const newAnonUser: AnonymousUser = {
                    ...anonUser,
                    votes: {
                        ...anonUser.votes,
                        [result.pollId]: {
                            voteId: result.id,
                            optionId: result.optionId,
                        },
                    },
                };
                setAnonUser(newAnonUser);

                const newPoll = { ...poll };
                newPoll.options[option - 1].votes++;
                setPoll(newPoll);
            } else {
                // Maybe we should enable voting again for a retry?
                Logger.error(JSON.stringify(result));
            }
        }
    };
}

export default PollController;
